import React, { useEffect, useState } from 'react';
import { TextField, Button, Typography, Grid, IconButton } from '@mui/material';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import { resetPassword } from '../../redux/user/user.actions';
import http from '../../services/http';
import styles from './resetPassword.module.css';
import { ArrowBackIosNewSharp } from '@mui/icons-material';

const ResetPassword = (props) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);
  const [isValidatingToken, setIsValidatingToken] = useState(false);

  useEffect(() => {
    const tokenFromUrl = props.match.params.token;
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
      validateToken(tokenFromUrl);
    }
    // E-mail vem no link (?email=...); fallback para o localStorage de quando a
    // solicitação foi feita neste navegador. O backend exige o campo "email".
    let emailFromUrl = '';
    try {
      emailFromUrl = new URLSearchParams(props.location?.search || '').get('email') || '';
    } catch {}
    setEmail(emailFromUrl || localStorage.getItem('recoverEmail') || '');
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (props.updateSuccess === 'passwordChanged') {
      setPasswordChanged(true);
    } else if (props.authError && !isValidatingToken) {
      // Mostra mensagem de erro específica do backend
      let errorMessage = 'Erro ao redefinir senha';
      if (props.authError.errorMessage) {
        errorMessage = props.authError.errorMessage;
      } else if (props.authError.message) {
        errorMessage = props.authError.message;
      }
      
      toast.error(errorMessage, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
    // eslint-disable-next-line
  }, [props.updateSuccess, props.authError, isValidatingToken]);

  const validateToken = async (tokenToValidate) => {
    setIsValidatingToken(true);
    
    // Validação básica do token
    if (!tokenToValidate || tokenToValidate.length < 10) {
      setTokenValid(false);
      toast.error('Token inválido. Solicite um novo link de redefinição.', {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      setIsValidatingToken(false);
      return;
    }

    try {
      await http.get(`/password/find/${tokenToValidate}`);
      setTokenValid(true);
    } catch (error) {
      setTokenValid(false);
      toast.error('Token inválido ou expirado. Solicite um novo link de redefinição.', {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } finally {
      setIsValidatingToken(false);
    }
  };

  const handleResetPassword = () => {
    // E-mail é obrigatório no backend; garante que está presente.
    const trimmedEmail = (email || '').trim();
    if (!trimmedEmail) {
      toast.error('Informe o e-mail da conta para redefinir a senha.', {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return;
    }

    // Validação de senha mínima
    if (password.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres!', {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return;
    }

    if (password !== confirmPassword) {
      toast.error(`${confirmPassword ? 'A senha de confirmação e a nova senha não coincidem!' : 'Confirme sua senha atual'}`, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return;
    }

    props.resetPassword(password, confirmPassword, token, trimmedEmail);
  };

  return (
    <Grid sx={{ overflowY: 'auto', padding: 0, height: '100vh' }} className={styles.reset}>
      <Grid className="header" item sx={{ paddingLeft: 2 }}>
        <IconButton
          edge="start"
          color="inherit"
          aria-label="voltar"
          onClick={() => {
            props.history.push('/login');
          }}
        >
          <ArrowBackIosNewSharp />
        </IconButton>
        <Typography variant="h4" sx={{ color: 'white' }}>Redefinir Senha</Typography>
      </Grid>
      {/*
        Os quatro estados desta tela abriam com `xs={12}` num `container`, que
        é prop de item -- o MUI avisava no console. Aqui tirar a prop e não
        repor nada MUDARIA a largura: com `spacing={3}` o container ganha
        `width: calc(100% + 24px)`, e era o `maxWidth: 100%` vindo do `xs` que
        segurava isso dentro da tela. Ver `src/avisosDoConsole.test.js`.
      */}
      {passwordChanged ? (
        <Grid container flexDirection={'column'} alignItems={'center'} spacing={3} sx={{ flexBasis: '100%', flexGrow: 0, maxWidth: '100%', marginTop: 1 }}>
          <Typography variant="h4">Sua senha foi alterada com sucesso!</Typography>
        </Grid>
      ) : isValidatingToken ? (
        <Grid container flexDirection={'column'} alignItems={'center'} spacing={3} sx={{ flexBasis: '100%', flexGrow: 0, maxWidth: '100%', marginTop: 1 }}>
          <Typography variant="h6">Validando token...</Typography>
        </Grid>
      ) : !tokenValid ? (
        <Grid container flexDirection={'column'} alignItems={'center'} spacing={3} sx={{ flexBasis: '100%', flexGrow: 0, maxWidth: '100%', marginTop: 1 }}>
          <Typography variant="h6" color="error">Token inválido ou expirado</Typography>
          <Typography variant="body1" sx={{ marginTop: 2 }}>
            Este link de redefinição de senha não é mais válido. 
            <br />
            <Button 
              variant="text" 
              onClick={() => props.history.push('/login')}
              sx={{ marginTop: 1 }}
            >
              Voltar ao login para solicitar um novo link
            </Button>
          </Typography>
        </Grid>
      ) : (
        <Grid container flexDirection={'column'} alignItems={'center'} spacing={3} sx={{ flexBasis: '100%', flexGrow: 0, maxWidth: '100%', marginTop: 1 }}>
          <Grid item xs={12}>
            <TextField
              label="E-mail"
              type="email"
              value={email}
              sx={{ width: '50vw' }}
              onChange={(e) => setEmail(e.target.value)}
              error={email.length === 0}
              helperText={email.length === 0 ? "Informe o e-mail da conta" : ""}
            />
          </Grid>
          <Grid item sx={{ margin: 0, padding: 0 }} xs={12} >
            <TextField
              label="Nova Senha"
              type="password"
              sx={{ width: '50vw' }}
              onChange={(e) => setPassword(e.target.value)}
              error={password.length > 0 && password.length < 6}
              helperText={password.length > 0 && password.length < 6 ? "A senha deve ter pelo menos 6 caracteres" : ""}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Confirmar Nova Senha"
              type="password"
              sx={{ width: '50vw' }}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={confirmPassword.length > 0 && password !== confirmPassword}
              helperText={confirmPassword.length > 0 && password !== confirmPassword ? "As senhas não coincidem" : ""}
            />
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" onClick={handleResetPassword} fullWidth sx={{ width: '25vw' }}>
              Redefinir Senha
            </Button>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

const mapStateToProps = (state) => {
  const { user } = state;
  const { updateSuccess, authError } = user;
  return { updateSuccess, authError };
};

const mapDispatchToProps = (dispatch) => ({
  resetPassword: (password, password_confirmation, token, email) => dispatch(resetPassword(password, password_confirmation, token, email)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ResetPassword);